/**
 * PricingPage - Refactored with premium cards and "Why this plan" bullets
 */

import React from "react";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { ScrollReveal } from "@/components/landing/ScrollReveal";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/Section";
import { Container } from "@/components/marketing/Container";
import { FeatureGrid } from "@/components/marketing/FeatureGrid";
import { ConversionCTA } from "@/components/marketing/ConversionCTA";
import { redirectToStripeCheckout } from "@/lib/stripe";
import { PRICING_PLANS, FEATURE_COMPARISON, TRIAL_DAYS } from "@/config/pricing";

export default function PricingPage() {
  const faqs = [
    {
      question: "What happens after my free trial?",
      answer: `After your ${TRIAL_DAYS}-day free trial, you can choose to upgrade to a paid plan. If you don't upgrade, your account will be paused but your data will be preserved for 30 days.`
    },
    {
      question: "Can I change plans later?",
      answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle."
    },
    {
      question: "Is there a contract?",
      answer: "No long-term contracts. Monthly plans can be cancelled anytime. We focus on delivering value, not locking you in."
    },
    {
      question: "Do you offer discounts for startups?",
      answer: "Yes. Founder / Early Access pricing is available on Base while active. Contact us at info@decisionsalesos.com for custom commercial requests."
    }
  ];

  const whyThisPlan = {
    Base: [
      "Perfect for solo B2B sales professionals",
      "Get decision clarity without team complexity",
      "Start with 500 leads, scale as you grow"
    ],
    Pro: [
      "Best for growing sales teams",
      "Advanced prioritization for complex pipelines",
      "Team visibility without micromanagement"
    ]
  };

  return (
    <MarketingLayout showBackLink>
      {/* Hero */}
      <Section spacing="md">
        <Container size="lg" center>
          <ScrollReveal>
            <h1 className="text-[var(--font-display)] font-semibold tracking-[var(--tracking-tighter)] mb-6 leading-[var(--leading-display)] text-center">
              Simple, Transparent Pricing
            </h1>
            <p className="text-[var(--font-body-lg)] text-muted-foreground max-w-2xl mx-auto mb-8 text-center leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
              Start free. Upgrade when you're ready. No hidden fees.
            </p>
            
            {/* Trial Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              {TRIAL_DAYS} days free trial
            </div>
          </ScrollReveal>
        </Container>
      </Section>

      {/* Pricing Cards */}
      <Section spacing="md">
        <Container size="xl">
          <FeatureGrid cols={2} gap="lg">
            {PRICING_PLANS.map((plan) => (
              <ScrollReveal key={plan.name} delay={plan.popular ? 0.1 : 0}>
                {/* Single border only (no NotchedFrame, no always-on ring) */}
                <div className={`relative h-full rounded-2xl border transition-all p-8 flex flex-col ${
                  plan.popular 
                    ? 'border-primary shadow-xl bg-gradient-to-b from-card to-primary/5' 
                    : 'border-border hover:border-primary/50 bg-card'
                }`}>
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full shadow-lg">
                        Consigliato
                      </div>
                    )}
                    
                    {plan.badge && (
                      <div className="absolute -top-3 right-4 px-3 py-1 bg-muted/60 border border-border/40 text-muted-foreground text-xs font-medium rounded-full">
                        {plan.badge}
                      </div>
                    )}
                    
                    <div className="mb-6">
                      <h3 className="text-2xl font-bold mb-2 tracking-[var(--tracking-tight)]">{plan.name}</h3>
                      <p className="text-sm font-medium text-primary mb-1 tracking-[var(--tracking-normal)]">{plan.subtitle}</p>
                      <p className="text-sm text-muted-foreground leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">{plan.description}</p>
                    </div>
                    
                    <div className="mb-6">
                      {plan.price !== null ? (
                        <div className="space-y-1">
                          {plan.originalPrice && (
                            <div className="flex items-center gap-2">
                              <span className="text-lg text-muted-foreground line-through">
                                {plan.originalPrice.toFixed(2)}{plan.currency}
                              </span>
                            </div>
                          )}
                          <div className="flex items-baseline gap-2">
                            <span className="text-5xl font-bold tracking-[var(--tracking-tighter)]">{plan.price.toFixed(2)}</span>
                            <span className="text-muted-foreground text-lg">{plan.currency}</span>
                            <span className="text-muted-foreground text-sm ml-1">{plan.period}</span>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <span className="text-3xl font-bold">{plan.period}</span>
                        </div>
                      )}
                    </div>
                    
                    {plan.highlight && (
                      <div className="mb-4 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-medium text-center">
                        {plan.highlight}
                      </div>
                    )}
                    
                    <Button 
                      className={`w-full mb-8 ${plan.popular ? 'shadow-lg' : ''}`}
                      variant={plan.popular ? "landing" : "outline"}
                      size="lg"
                      onClick={redirectToStripeCheckout}
                      asChild={false}
                    >
                      <>
                        {plan.cta}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    </Button>
                    
                    {/* Why This Plan */}
                    {whyThisPlan[plan.name as keyof typeof whyThisPlan] && (
                      <div className="mb-6 p-4 rounded-lg bg-secondary/30 border border-border/40">
                        <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Why this plan</p>
                        <ul className="space-y-2">
                          {whyThisPlan[plan.name as keyof typeof whyThisPlan].map((reason, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs text-foreground/80">
                              <Check className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
                              <span>{reason}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <ul className="space-y-3 flex-1">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm">
                          <Check className={`w-5 h-5 ${plan.popular ? 'text-primary' : 'text-primary/70'} mt-0.5 flex-shrink-0`} />
                          <span className="text-foreground/90 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
              </ScrollReveal>
            ))}
          </FeatureGrid>
        </Container>
      </Section>

      {/* Feature Comparison */}
      <Section variant="secondary" spacing="lg">
        <Container size="xl">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-[var(--font-h2)] font-semibold mb-4 tracking-[var(--tracking-tighter)] leading-[var(--leading-headline)]">
                Compare Plans
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                See how our Decision Engine, revenue focus, and prioritization features scale with your needs
              </p>
            </div>
          </ScrollReveal>
          
          <ScrollReveal delay={0.1}>
            <div className="rounded-2xl border bg-card overflow-hidden shadow-lg">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-5 font-semibold text-sm">Feature</th>
                      <th className="text-center p-5 font-semibold text-sm min-w-[140px]">Base</th>
                      <th className="text-center p-5 font-semibold text-sm bg-primary/10 min-w-[140px]">Pro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(
                      FEATURE_COMPARISON.reduce((acc, row) => {
                        if (!acc[row.category]) {
                          acc[row.category] = [];
                        }
                        acc[row.category].push(row);
                        return acc;
                      }, {} as Record<string, typeof FEATURE_COMPARISON>)
                    ).map(([category, rows]) => (
                      <React.Fragment key={category}>
                        <tr className="border-b bg-muted/20">
                          <td colSpan={3} className="p-3 px-5 font-semibold text-xs uppercase tracking-wider text-muted-foreground">
                            {category}
                          </td>
                        </tr>
                        {rows.map((row, i) => (
                          <tr key={`${category}-${i}`} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                            <td className="p-4 px-5 text-sm font-medium">{row.feature}</td>
                            <td className="p-4 text-center text-sm">
                              {typeof row.base === 'boolean' ? (
                                row.base ? (
                                  <Check className="w-5 h-5 text-primary mx-auto" />
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )
                              ) : (
                                <span className="text-foreground/80">{row.base}</span>
                              )}
                            </td>
                            <td className="p-4 text-center text-sm bg-primary/5">
                              {typeof row.pro === 'boolean' ? (
                                row.pro ? (
                                  <Check className="w-5 h-5 text-primary mx-auto" />
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )
                              ) : (
                                <span className="text-foreground/80 font-medium">{row.pro}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </ScrollReveal>
        </Container>
      </Section>

      {/* FAQ */}
      <Section spacing="lg">
        <Container size="lg">
          <ScrollReveal>
            <h2 className="text-2xl md:text-3xl font-semibold text-center mb-12 tracking-[var(--tracking-tighter)] leading-[var(--leading-headline)]">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
          
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div className="p-6 rounded-xl border bg-card hover:border-primary/50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-primary">?</span>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2 tracking-[var(--tracking-tight)]">{faq.question}</h3>
                      <p className="text-sm text-muted-foreground leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* CTA */}
      <ConversionCTA 
        headline="Start Making Better Sales Decisions"
        bullets={[
          'Join sales professionals who use Decision Sales OS',
          'Focus on revenue-generating actions, not busywork',
        ]}
      />
    </MarketingLayout>
  );
}

